/**
 * 앱 기록 (page-partner-app-records.php) — 목록의 줄을 누르면 상세를 받아 공통 모달(#record-modal)에 그린다.
 *
 * 목록 · 검색 · 쪽 이동은 서버가 그린다. 여기서는 상세만 맡는다. 서버(Partner_App_Records::handle_detail)가 기록을
 * 머리(이름 · 상태 · 꼬리표 · 날짜)와 섹션들로 정리해 주므로 종류(상담 · 동의서 · 협찬 · 채팅)를 가리지 않고 그린다.
 *   rows   라벨 · 값 줄 (값에 url 이 있으면 링크)
 *   images 서명 · 참고 사진 · 스케치 — 주소는 partner 가 중계하는 admin-ajax 주소, 누르면 새 창
 *   text   메모 같은 긴 글 + 덧붙임
 *   chat   대화 — 손님은 왼쪽, 직원은 오른쪽. 번역은 원문 아래
 * 값은 전부 textContent 로 넣는다(손님이 쓴 글이다).
 *
 * 프린트 · PDF 저장([data-record-print])은 새 탭 없이 이 화면에 머문 채 인쇄한다. 링크의 href 는 지금도
 * 인쇄 문서 주소(print_url)에 auto=print|pdf 를 붙여 채워 두지만(가운데 클릭 · 수정 키 · 손가락 환경 · iOS 의
 * 새 탭 폴백용), 보통 클릭은 가로챈다 — 화면 밖 숨은 iframe 에 print_url&embed=1 을 실어 불러오고, 문서 뿌리에
 * data-print-ready 가 있으면 그 iframe 을 인쇄한다(PDF 는 파일 이름을 부모 · iframe 제목에 둘 다 반영 — 사파리는
 * 부모 제목을 PDF 이름으로 쓴다). 없으면(세션 만료 등 오류 페이지) 본문 글을 footer 안내(.notice)로 보여 준다.
 * 불러오는 동안 두 버튼 모두 다시 못 누르게 막고(눌린 쪽은 "준비 중…") 인쇄 창을 띄우면 되돌린다. iframe 은
 * 다음 인쇄 때나 모달을 닫거나 다른 기록을 열 때(setPrintUrl) 치운다 — afterprint 에서는 제목만 되돌린다.
 * 양식은 매장 사이트 wp-admin 의 것을 그대로 받아 쓰므로 이 모달의 모양과는 별개다. 상세를 받기 전에는 감춘다.
 */

import { postForm, setNotice } from './partnerForm';

/** 요소 하나 — 클래스 · 글 */
const el = (tag, className = '', text = '') => {
  const node = document.createElement(tag);

  if (className) {
    node.className = className;
  }

  if (text) {
    node.textContent = text;
  }

  return node;
};

const renderRows = (section) => {
  const list = el('dl', 'record__rows');

  section.rows.forEach((row) => {
    const item = el('div', 'record__row');
    const value = el('dd', 'record__value');

    if (row.url) {
      const link = el('a', 'linktext', row.value);
      link.href = row.url;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      value.append(link);
    } else {
      value.textContent = row.value;
    }

    item.append(el('dt', 'record__label', row.label), value);
    list.append(item);
  });

  return list;
};

const renderImages = (section) => {
  const list = el('ul', 'record__images');

  section.images.forEach((image) => {
    const item = el('li', 'record__image');
    const link = el('a', 'record__image-link');
    const img = el('img');

    link.href = image.url;
    link.target = '_blank';
    link.rel = 'noopener';
    img.src = image.url;
    img.alt = image.label;
    img.loading = 'lazy';
    img.decoding = 'async';
    // 파일이 지워졌거나 중계에 실패한 그림은 자리만 남기지 않고 안내로 바꾼다
    img.addEventListener('error', () => {
      link.replaceWith(el('span', 'record__image-missing', '그림을 불러오지 못했습니다'));
    });

    link.append(img);
    item.append(link, el('span', 'record__image-label', image.label));
    list.append(item);
  });

  return list;
};

const renderText = (section) => {
  const wrap = el('div', 'record__text');

  wrap.append(el('p', 'record__text-body', section.text));

  if (section.note) {
    wrap.append(el('p', 'record__note', section.note));
  }

  return wrap;
};

const renderChat = (section) => {
  const wrap = el('div', 'record__chat');
  const list = el('ol', 'chatlog');

  section.messages.forEach((message) => {
    const item = el('li', 'chatlog__item');
    const bubble = el('div', 'chatlog__bubble');
    const who = 'staff' === message.sender ? message.name || '직원' : '손님';

    item.dataset.sender = message.sender;
    item.append(el('span', 'chatlog__who', [who, message.time].filter(Boolean).join(' · ')));

    if (message.card) {
      bubble.append(el('span', 'chatlog__card', message.card));
    }

    if (message.text) {
      bubble.append(el('p', 'chatlog__text', message.text));
    }

    if (message.translated) {
      bubble.append(el('p', 'chatlog__translated', message.translated));
    }

    item.append(bubble);
    list.append(item);
  });

  wrap.append(list);

  if (section.note) {
    wrap.append(el('p', 'record__note', section.note));
  }

  return wrap;
};

const RENDERERS = { rows: renderRows, images: renderImages, text: renderText, chat: renderChat };

/** 기록 하나를 모달 body 에 그린다 */
const renderRecord = (body, record) => {
  const head = el('div', 'record__head');
  const badges = el('div', 'record__badges');

  head.append(el('p', 'record__name', record.name || '이름 없음'));

  if (record.phone) {
    head.append(el('p', 'record__phone', record.phone));
  }

  if (record.status.label) {
    badges.append(el('span', `badge ${record.status.badge}`.trim(), record.status.label));
  }

  record.tags.forEach((tag) => badges.append(el('span', 'badge', tag)));

  if (record.date) {
    badges.append(el('span', 'record__date', record.date));
  }

  head.append(badges);
  body.replaceChildren(head);

  record.sections.forEach((section) => {
    const render = RENDERERS[section.type];

    if (!render) {
      return;
    }

    const block = el('section', 'record__section');

    block.append(el('h3', 'record__section-title', section.title), render(section));
    body.append(block);
  });
};

/** 받아 오는 동안 · 실패했을 때의 한 줄 */
const renderState = (body, message, state = '') => {
  const line = el('p', 'record__state', message);

  if (state) {
    line.dataset.state = state;
  }

  body.replaceChildren(line);
};

/** 숨은 iframe 인쇄가 불안정한 환경(손가락 · iOS) — 이런 환경은 새 탭 폴백에 맡긴다 */
const isUnstablePrintEnv = () => {
  const ua = navigator.userAgent || '';
  const iOS =
    /iPhone|iPad|iPod/.test(ua) ||
    ('MacIntel' === navigator.platform && navigator.maxTouchPoints > 1);

  return iOS || (window.matchMedia && window.matchMedia('(pointer: coarse)').matches);
};

const InitPartnerAppRecords = () => {
  const root = document.querySelector('[data-app-records]');
  const dialog = document.getElementById('record-modal');
  const body = dialog?.querySelector('[data-record-body]');
  const title = dialog?.querySelector('.modal__title');

  if (!root || !dialog || !body || !title) {
    return;
  }

  const printLinks = Array.from(dialog.querySelectorAll('[data-record-print]'));
  const printStatus = dialog.querySelector('[data-record-print-status]');

  let printBaseUrl = ''; // embed=1 을 붙일 원본 주소 (href 에는 이미 auto=mode 가 붙어 있다)
  let printFrame = null; // 숨은 인쇄 iframe. printFrame !== iframe 이면 그 iframe 은 버려진 요청이다
  let restoreTitle = null; // PDF 로 바꾸기 전 부모 document.title
  let printBusy = false;

  /** 버튼 두 개 다 잠그고, 누른 쪽 글자만 "준비 중…" 으로 */
  const setPrintBusy = (activeLink) => {
    printBusy = true;

    printLinks.forEach((link) => {
      link.setAttribute('aria-disabled', 'true');
    });

    activeLink.dataset.originalLabel = activeLink.textContent;
    activeLink.textContent = '준비 중…';
    activeLink.setAttribute('aria-busy', 'true');
  };

  const clearPrintBusy = () => {
    printBusy = false;

    printLinks.forEach((link) => {
      link.removeAttribute('aria-disabled');
      link.removeAttribute('aria-busy');

      if (link.dataset.originalLabel) {
        link.textContent = link.dataset.originalLabel;
        delete link.dataset.originalLabel;
      }
    });
  };

  /** 진행 중이던 iframe · 제목 · 버튼 상태를 지운다(성공 · 실패 · afterprint · 모달 닫힘 어디서나 부른다) */
  const cleanupPrintFrame = () => {
    if (printFrame) {
      printFrame.remove();
      printFrame = null;
    }

    if (null !== restoreTitle) {
      document.title = restoreTitle;
      restoreTitle = null;
    }

    clearPrintBusy();
  };

  /** 인쇄 문서 주소를 버튼에 채운다. 빈 주소면 감춘다. 이전 인쇄가 남아 있으면 정리한다 */
  const setPrintUrl = (url) => {
    printBaseUrl = url;
    cleanupPrintFrame();
    setNotice(printStatus, '');

    printLinks.forEach((link) => {
      link.hidden = !url;
      link.href = url ? `${url}&auto=${link.dataset.recordPrint}` : '#';
    });
  };

  // 모달을 닫으면(Esc · 바탕 · 닫기 버튼) 남은 iframe · 상태를 지운다. 다른 기록을 열 때는 아래 row 클릭에서
  // setPrintUrl('') 을 부르므로 같은 정리를 거친다
  dialog.addEventListener('close', () => setPrintUrl(''));

  /** 화면 밖 숨은 iframe 으로 인쇄 문서를 불러와 인쇄 창을 띄운다 */
  const printRecord = (link, mode) => {
    cleanupPrintFrame(); // 못 끝낸 이전 시도가 있으면 먼저 치운다
    setNotice(printStatus, '');
    setPrintBusy(link);

    const iframe = document.createElement('iframe');

    iframe.style.cssText =
      'position:fixed; right:0; bottom:0; width:0; height:0; border:0; visibility:hidden;';
    iframe.setAttribute('aria-hidden', 'true');
    iframe.tabIndex = -1;

    const fail = (message) => {
      if (printFrame !== iframe) {
        return; // 그 사이 모달이 닫혔거나 다른 기록을 열어 이미 버려진 요청
      }

      cleanupPrintFrame();
      setNotice(
        printStatus,
        message || '인쇄 문서를 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.'
      );
    };

    iframe.addEventListener('error', () => fail());

    iframe.addEventListener('load', () => {
      if (printFrame !== iframe) {
        return;
      }

      let doc;

      try {
        doc = iframe.contentDocument;
      } catch (error) {
        fail();
        return;
      }

      if ('about:blank' === doc?.URL) {
        return; // 아직 인쇄 문서가 아니다
      }

      if (!doc?.documentElement?.hasAttribute('data-print-ready')) {
        // 오류 페이지(wp_die) — 짧은 안내 글만 담겨 있다
        fail(doc?.body?.textContent?.trim());
        return;
      }

      const fileName = doc.documentElement.getAttribute('data-file-name') || '';
      const win = iframe.contentWindow;

      if ('pdf' === mode && fileName) {
        // 사파리는 PDF 로 저장할 때 부모(최상위) 문서의 제목을 파일 이름으로 쓴다
        restoreTitle = document.title;
        document.title = fileName;
        doc.title = fileName;
      }

      // iframe 은 여기서 지우지 않는다 — 사파리는 인쇄 창이 떠 있는 동안 afterprint 를 먼저 보내기도 해서, 그때
      // 지우면 인쇄가 끊긴다. 남은 iframe 은 다음 인쇄 · 다른 기록 · 모달 닫힘(cleanupPrintFrame)에서 치운다
      win.addEventListener(
        'afterprint',
        () => {
          if (printFrame === iframe && null !== restoreTitle) {
            document.title = restoreTitle;
            restoreTitle = null;
          }
        },
        { once: true }
      );
      win.focus();
      win.print();

      // 크롬은 인쇄 창이 닫혀야 print() 가 끝나고, 다른 브라우저는 곧바로 끝나지만 인쇄 창이 화면을 막고 있다.
      // afterprint 가 오지 않는 브라우저에서도 버튼이 잠긴 채 남지 않게 여기서 푼다
      clearPrintBusy();
    });

    // 주소를 먼저 넣고 붙인다 — 빈 iframe 을 붙이면 about:blank 의 load 가 먼저 와서 오류 페이지로 읽힌다
    printFrame = iframe;
    iframe.src = `${printBaseUrl}&embed=1`;
    document.body.append(iframe);
  };

  printLinks.forEach((link) => {
    link.addEventListener('click', (event) => {
      if (printBusy) {
        event.preventDefault();
        return;
      }

      // 수정 키 · 가운데/오른쪽 클릭 · 주소 없음 · 손가락 환경(iOS 포함)은 href 의 새 탭 폴백에 맡긴다
      if (
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey ||
        0 !== event.button ||
        !printBaseUrl ||
        isUnstablePrintEnv()
      ) {
        return;
      }

      event.preventDefault();
      printRecord(link, link.dataset.recordPrint);
    });
  });

  // 늦게 온 응답이 그 사이 연 다른 기록을 덮지 않게 마지막 요청만 그린다
  let latest = 0;

  root.addEventListener('click', async (event) => {
    const row = event.target.closest('[data-record-id]');

    if (!row) {
      return;
    }

    const ticket = ++latest;

    title.textContent = '기록';
    setPrintUrl('');
    renderState(body, '불러오는 중…');
    dialog.showModal();

    try {
      const data = await postForm(root, root.dataset.action, {
        kind: root.dataset.kind,
        id: row.dataset.recordId,
      });

      if (ticket !== latest) {
        return;
      }

      title.textContent = data.kind_label || '기록';
      renderRecord(body, data.record);
      setPrintUrl(data.print_url || '');
      body.scrollTop = 0;
    } catch (error) {
      if (ticket === latest) {
        renderState(body, error.message, 'error');
      }
    }
  });
};

export default InitPartnerAppRecords;
